import courseraPlusProductVariants, {
  courseraLiteProductVariants,
  isMonthly,
} from 'bundles/coursera-plus-common/constants/CourseraPlusProductVariant';
import type SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';
import { useGetProductPrices } from 'bundles/ownable-products-common/hooks/useGetProductPrices';
import ProductType from 'bundles/payments-common/common/ProductType';
import SubscriptionStatus from 'bundles/subscriptions-common/utils/SubscriptionStatus';

export const isActiveOrFreeTrialCourseraPlusSubscription = (subscription: SubscriptionsV1) =>
  subscription.isCourseraPlusSubscription &&
  subscription.productSku &&
  isMonthly(subscription.productSku) &&
  (subscription.status === SubscriptionStatus.ACTIVE || subscription.status === SubscriptionStatus.FREE_TRIAL);

const ONE_DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
export const getEligibleCourseraPlusMonthlySubscription = (subscriptions: Array<SubscriptionsV1>) => {
  const subscription = subscriptions.find(isActiveOrFreeTrialCourseraPlusSubscription);
  const now = new Date().getTime();
  const isWithinOneDayOfRenewal = subscription && subscription.nextBillingAt - now < ONE_DAY_IN_MILLISECONDS;

  // If the subscription is within one day of renewal, we don't want to show the upgrade CTA.
  // This will avoid a bug where the payment processor is still charging them for c plus monthly
  // even after the user made an upgrade request.
  if (isWithinOneDayOfRenewal) {
    return undefined;
  }

  return subscription;
};

export const useGetCourseraLitePrice = (skip?: boolean) => {
  const products = [
    {
      productType: ProductType.COURSERA_TIER_LITE,
      productItemId: courseraLiteProductVariants.MONTHLY_WITH_FREE_TRIAL,
    },
  ];

  const { productPrices: courseraLitePrices } = useGetProductPrices({ products, skip });

  return {
    courseraLitePrice: courseraLitePrices?.[0],
  };
};

export const useGetCourseraPlusMonthlyPrice = ({
  productItemId = courseraPlusProductVariants.MONTHLY_WITH_FREE_TRIAL,
  skip,
}: {
  productItemId?: string;
  skip?: boolean;
} = {}) => {
  const products = [
    {
      productType: ProductType.COURSERA_PLUS_SUBSCRIPTION,
      productItemId,
    },
  ];

  const { productPrices: courseraPlusPrices } = useGetProductPrices({ products, skip });

  return {
    courseraPlusMonthlyPrice: courseraPlusPrices?.[0],
  };
};

export const useGetCourseraPlusAnnualPrice = (skip?: boolean) => {
  const products = [
    {
      productType: ProductType.COURSERA_PLUS_SUBSCRIPTION,
      productItemId: courseraPlusProductVariants.ANNUAL_MIDCYCLE_UPGRADE,
    },
  ];

  const { productPrices: courseraPlusPrices } = useGetProductPrices({ products, skip });

  return {
    courseraPlusAnnualPrice: courseraPlusPrices?.[0],
  };
};
